import { sanitizeHTML } from '@/lib/utils'

interface SecFaqsProps {
	id: number
	heading?: {
		title?: string
	}
	content_collection?: {
		id: number
		site_content_collections_id: {
			title: string
			collection: {
				item: {
					title?: string // from site_content.title
					text?: string // from site_content.text
				}
			}[]
		}
	}[]
}

export const fields = [
	'heading.title',

	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
]
export default function SecFaqs({ data }: { data: SecFaqsProps }) {
	// Function to calculate text length for an FAQ item
	const calculateFaqTextLength = (faq: any) => {
		const titleItem = faq.site_content_collections_id.collection.find((c: any) => c.item.title)?.item
		const textItem = faq.site_content_collections_id.collection.find((c: any) => c.item.text)?.item
		
		const titleLength = (titleItem?.title || faq.site_content_collections_id.title || '').length
		const textLength = (textItem?.text || '').replace(/<[^>]*>/g, '').length // Remove HTML tags for length calculation
		
		return titleLength + textLength
	}

	// Function to estimate number of lines based on text length
	const estimateLines = (textLength: number) => {
		// Assuming ~60-70 characters per line on average
		return Math.ceil(textLength / 200)
	}

	// Helper to render a single FAQ item
	const renderFaqItem = (faq: any) => {
		const titleItem = faq.site_content_collections_id.collection.find(c => c.item.title)?.item
		const textItem = faq.site_content_collections_id.collection.find(c => c.item.text)?.item

		return (
			<div key={faq.id} className='space-y-6'>
				<h3>{titleItem?.title || faq.site_content_collections_id.title || ''}</h3>
				<div
					className='grid gap-8'
					dangerouslySetInnerHTML={sanitizeHTML(textItem?.text || '')}
				/>
			</div>
		)
	}

	// Group FAQs into sections: consecutive short FAQs are grouped together, long FAQs stand alone
	const groupFaqs = () => {
		if (!data?.content_collection) return []

		const groups: Array<{ type: 'full-width' | 'two-column'; faqs: any[] }> = []
		let currentGroup: any[] = []

		data.content_collection?.forEach((faq, index) => {
			const faqLength = calculateFaqTextLength(faq)
			const estimatedLines = estimateLines(faqLength)
			const isLongFaq = estimatedLines > 8

			if (isLongFaq) {
				// If we have accumulated short FAQs, save them as a two-column group
				if (currentGroup.length > 0) {
					groups.push({ type: 'two-column', faqs: currentGroup })
					currentGroup = []
				}
				// Add the long FAQ as a full-width group
				groups.push({ type: 'full-width', faqs: [faq] })
			} else {
				// Accumulate short FAQs
				currentGroup.push(faq)
			}

			// If this is the last item and we have accumulated short FAQs, save them
			if (index === (data.content_collection?.length ?? 0) - 1 && currentGroup.length > 0) {
				groups.push({ type: 'two-column', faqs: currentGroup })
			}
		})

		return groups
	}

	// Distribute FAQs into two columns for a two-column group
	const distributeTwoColumns = (faqs: any[]) => {
		const leftColumn: any[] = []
		const rightColumn: any[] = []
		let leftColumnLength = 0
		let rightColumnLength = 0

		faqs.forEach(faq => {
			const faqLength = calculateFaqTextLength(faq)
			
			if (leftColumnLength <= rightColumnLength) {
				leftColumn.push(faq)
				leftColumnLength += faqLength
			} else {
				rightColumn.push(faq)
				rightColumnLength += faqLength
			}
		})

		return { leftColumn, rightColumn }
	}

	const faqGroups = groupFaqs()

	return (
		<section id='faqs' className='SecFaqs py-16 2xl:py-32'>
			<div className='container'>
				<h2 className='text-center mb-12 md:mb-20'>
					{data?.heading?.title || 'Frequently Asked Questions'}
				</h2>

				<div className='space-y-12'>
					{faqGroups.map((group, groupIndex) => {
						if (group.type === 'full-width') {
							// Render full-width FAQ
							return (
								<div key={`group-${groupIndex}`}>
									{group.faqs.map(faq => renderFaqItem(faq))}
								</div>
							)
						} else {
							// Render two-column layout for short FAQs
							const { leftColumn, rightColumn } = distributeTwoColumns(group.faqs)
							return (
								<div key={`group-${groupIndex}`} className='grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-24'>
									{/* Left Column */}
									<div className='space-y-12'>
										{leftColumn.map(faq => renderFaqItem(faq))}
									</div>

									{/* Right Column */}
									<div className='space-y-12'>
										{rightColumn.map(faq => renderFaqItem(faq))}
									</div>
								</div>
							)
						}
					})}
				</div>
			</div>
		</section>
	)
}
